/**
 * News/NewsEditor.jsx
 * ════════════════════════════════════════════════════════════════
 * Форма створення/редагування новини. Модальне вікно.
 * Спільна для порталу і садочка — керується props scope/kgId.
 *
 * Props:
 *   scope     {string}   — 'portal' | 'kg'
 *   kgId      {string?}  — обов'язково для scope='kg'
 *   newsId    {string?}  — якщо задано — режим редагування
 *   onClose   {function}
 *   onSaved   {function} — (news) => void, викликається після збереження
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faSave, faThumbtack, faTrash,
    faTag, faFolder, faCalendar, faCalendarXmark,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { constr_uri } from '../../../../../services/apiClient';
import RichEditor from '../../../../../components/RichEditor/Richeditor';
import FileUploader from '../../../../../components/FileUpload/FileUploader';
import { newsAPI, newsCategoriesAPI, base64ToFile } from './newsAPI';
import NewsGallery from './NewsGallery';

// ── Допоміжні ─────────────────────────────────────────────────────────────────

function toLocalDatetimeInput(date) {
    if (!date) return '';
    var d = new Date(date);
    var pad = function(n) { return String(n).padStart(2, '0'); };
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
        + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
}

var EMPTY_NEWS = {
    title: '', excerpt: '', body: '',
    categoryId: '', tags: [],
    publishAt: new Date().toISOString(),
    expireAt: null,
    isPinned: false,
    status: 'draft',
    coverDocId: null, coverFileId: null,
};

// ── Тег-інпут ─────────────────────────────────────────────────────────────────

var TagInput = function({ tags, onChange }) {
    var [input, setInput] = useState('');

    var addTag = function() {
        var v = input.trim().toLowerCase();
        if (!v || tags.includes(v) || tags.length >= 10) { setInput(''); return; }
        onChange([...tags, v]);
        setInput('');
    };

    var removeTag = function(t) {
        onChange(tags.filter(function(x) { return x !== t; }));
    };

    return (
        <div>
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                border: '1px solid #ced4da', borderRadius: 8,
                padding: '8px 10px', minHeight: 42,
            }}>
                {tags.map(function(t) {
                    return (
                        <span key={t} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#eef2ff', color: '#4338ca',
                            padding: '2px 8px', borderRadius: 12, fontSize: 12,
                        }}>
                            #{t}
                            <FontAwesomeIcon
                                icon={faXmark}
                                style={{ cursor: 'pointer', fontSize: 10 }}
                                onClick={function() { removeTag(t); }}
                            />
                        </span>
                    );
                })}
                <input
                    type="text"
                    value={input}
                    onChange={function(e) { setInput(e.target.value); }}
                    onKeyDown={function(e) {
                        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
                        if (e.key === 'Backspace' && !input && tags.length) removeTag(tags[tags.length - 1]);
                    }}
                    onBlur={addTag}
                    placeholder={tags.length === 0 ? 'Тег і Enter...' : ''}
                    style={{ border: 'none', outline: 'none', flex: 1, minWidth: 80, fontSize: 13 }}
                />
            </div>
        </div>
    );
};

// ── Основний компонент ────────────────────────────────────────────────────────

var NewsEditor = function({ scope, kgId, newsId, onClose, onSaved }) {
    var [news, setNews]         = useState(EMPTY_NEWS);
    var [categories, setCategories] = useState([]);
    var [loading, setLoading]   = useState(!!newsId);
    var [saving, setSaving]     = useState(false);
    var [uploadingCover, setUploadingCover] = useState(false);
    var [coverPending, setCoverPending] = useState([]); // [{name,type,size,file:base64}] — вибрано, ще не на сервері
    var [errors, setErrors]     = useState({});

    var isEdit = !!newsId;

    useEffect(function() {
        newsCategoriesAPI.list('active')
            .then(function(list) { setCategories(Array.isArray(list) ? list : []); })
            .catch(function() { setCategories([]); });

        if (isEdit) {
            newsAPI.getById(scope, kgId, newsId)
                .then(function(data) {
                    setNews({
                        title:      data.title || '',
                        excerpt:    data.excerpt || '',
                        body:       data.body || '',
                        categoryId: data.categoryId || '',
                        tags:       data.tags || [],
                        publishAt:  data.publishAt,
                        expireAt:   data.expireAt,
                        isPinned:   !!data.isPinned,
                        status:     data.status,
                        coverDocId:  data.coverDocId,
                        coverFileId: data.coverFileId,
                    });
                })
                .catch(function() { toast.error('Не вдалося завантажити новину'); })
                .finally(function() { setLoading(false); });
        }
    }, []);

    var setField = function(field, value) {
        setNews(function(prev) { return { ...prev, [field]: value }; });
        setErrors(function(prev) { return { ...prev, [field]: null }; });
    };

    var validate = function() {
        var e = {};
        if (!news.title || !news.title.trim()) e.title = 'Обов\'язкове поле';
        if (news.title && news.title.length > 200) e.title = 'Максимум 200 символів';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    var handleSave = async function() {
        if (!validate()) return;
        setSaving(true);
        try {
            var payload = {
                title:      news.title.trim(),
                excerpt:    news.excerpt,
                body:       news.body,
                categoryId: news.categoryId || null,
                tags:       news.tags,
                publishAt:  news.publishAt,
                expireAt:   news.expireAt,
                isPinned:   news.isPinned,
            };

            var saved;
            if (isEdit) {
                saved = await newsAPI.update(scope, kgId, newsId, payload);
            } else {
                saved = await newsAPI.create(scope, kgId, payload);

                // Якщо перед створенням вже обрали обкладинку — вантажимо її
                // послідовно одразу після отримання id нової новини
                if (coverPending.length > 0) {
                    try {
                        var pf = coverPending[0];
                        var file = base64ToFile(pf.file, pf.name, pf.type);
                        var coverResult = await newsAPI.uploadCover(scope, kgId, saved._id, file);
                        saved.coverDocId  = coverResult.coverDocId;
                        saved.coverFileId = coverResult.coverFileId;
                    } catch (coverErr) {
                        toast.error('Новину створено, але обкладинку завантажити не вдалося');
                    }
                }
            }

            toast.success(isEdit ? 'Новину оновлено' : 'Новину створено');
            onSaved && onSaved(saved);
        } catch (err) {
            toast.error('Помилка збереження новини');
        } finally {
            setSaving(false);
        }
    };

    // Викликається FileUploader-ом одразу після вибору файлу (значення — base64)
    var handleCoverPick = async function(filesArr) {
        var picked = filesArr && filesArr[0];
        if (!picked) return;

        if (!isEdit) {
            // Новина ще не створена — просто запам'ятовуємо, завантажимо
            // на сервер одразу після збереження (в handleSave)
            setCoverPending([picked]);
            return;
        }

        // Новина вже існує — вантажимо одразу
        setUploadingCover(true);
        try {
            var file = base64ToFile(picked.file, picked.name, picked.type);
            var result = await newsAPI.uploadCover(scope, kgId, newsId, file);
            setField('coverDocId', result.coverDocId);
            setField('coverFileId', result.coverFileId);
            toast.success('Обкладинку завантажено');
        } catch (err) {
            toast.error('Помилка завантаження обкладинки');
        } finally {
            setUploadingCover(false);
        }
    };

    var handleCoverDelete = async function() {
        if (isEdit) {
            try {
                await newsAPI.deleteCover(scope, kgId, newsId);
            } catch (err) {
                toast.error('Помилка видалення обкладинки');
                return;
            }
        }
        setField('coverDocId', null);
        setField('coverFileId', null);
        setCoverPending([]);
    };

    var coverSrc = news.coverDocId && news.coverFileId
        ? constr_uri('docfile?doc_id=' + news.coverDocId + '&file_id=' + news.coverFileId)
        : null;

    return (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered modal-lg" onClick={function(e) { e.stopPropagation(); }}>
                <div className="modal-content" style={{ borderRadius: 14, overflow: 'hidden' }}>

                    {/* Хедер */}
                    <div className="modal-header" style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <h5 className="modal-title" style={{ fontWeight: 700 }}>
                            {isEdit ? 'Редагування новини' : 'Нова новина'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    {/* Тіло */}
                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: 24 }}>
                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                        ) : (
                            <>
                                {/* Обкладинка */}
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 8 }}>
                                        Обкладинка
                                    </label>

                                    {coverSrc ? (
                                        // Вже завантажено на сервер — простий перегляд + видалення
                                        <div style={{
                                            width: '100%', height: 180, borderRadius: 10,
                                            position: 'relative', overflow: 'hidden',
                                        }}>
                                            <img src={coverSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <button
                                                type="button"
                                                onClick={handleCoverDelete}
                                                style={{
                                                    position: 'absolute', top: 8, right: 8,
                                                    width: 30, height: 30, borderRadius: '50%',
                                                    background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                                            </button>
                                            {uploadingCover && (
                                                <div style={{
                                                    position: 'absolute', inset: 0,
                                                    background: 'rgba(255,255,255,0.7)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <Spinner animation="border" size="sm" variant="primary" />
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        // Ще не завантажено — вибір файлу з превью через FileUploader
                                        <FileUploader
                                            label=""
                                            description="Зображення JPG, PNG. Максимальний розмір 8 МБ."
                                            accept="image/*"
                                            multiple={false}
                                            maxCount={1}
                                            maxSize={8 * 1024 * 1024}
                                            value={coverPending}
                                            onChange={handleCoverPick}
                                            asBase64={true}
                                            editable={!uploadingCover}
                                            disabled={uploadingCover}
                                        />
                                    )}
                                </div>

                                {/* Заголовок */}
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 6 }}>
                                        Заголовок *
                                    </label>
                                    <input
                                        type="text"
                                        className={'form-control' + (errors.title ? ' is-invalid' : '')}
                                        value={news.title}
                                        onChange={function(e) { setField('title', e.target.value); }}
                                        maxLength={200}
                                        placeholder="Заголовок новини"
                                        style={{ height: 42, fontSize: 15 }}
                                    />
                                    {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                                </div>

                                {/* Короткий опис */}
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 6 }}>
                                        Короткий опис
                                        <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>(для картки у списку)</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        value={news.excerpt}
                                        onChange={function(e) { setField('excerpt', e.target.value); }}
                                        maxLength={400}
                                        rows={2}
                                        placeholder="Коротко про що новина..."
                                        style={{ fontSize: 14, resize: 'vertical' }}
                                    />
                                    <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 3 }}>
                                        {news.excerpt.length} / 400
                                    </div>
                                </div>

                                {/* Категорія + Теги */}
                                <div className="row" style={{ marginBottom: 16 }}>
                                    <div className="col-md-6">
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 6 }}>
                                            <FontAwesomeIcon icon={faFolder} style={{ marginRight: 5, fontSize: 11 }} />
                                            Категорія
                                        </label>
                                        <select
                                            className="form-select"
                                            value={news.categoryId || ''}
                                            onChange={function(e) { setField('categoryId', e.target.value); }}
                                            style={{ height: 42, fontSize: 14 }}
                                        >
                                            <option value="">Без категорії</option>
                                            {(Array.isArray(categories) ? categories : []).map(function(c) {
                                                return <option key={c._id} value={c._id}>{c.name}</option>;
                                            })}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 6 }}>
                                            <FontAwesomeIcon icon={faTag} style={{ marginRight: 5, fontSize: 11 }} />
                                            Теги
                                        </label>
                                        <TagInput tags={news.tags} onChange={function(t) { setField('tags', t); }} />
                                    </div>
                                </div>

                                {/* Публікація: дати */}
                                <div className="row" style={{ marginBottom: 16 }}>
                                    <div className="col-md-6">
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 6 }}>
                                            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 5, fontSize: 11 }} />
                                            Дата публікації
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={toLocalDatetimeInput(news.publishAt)}
                                            onChange={function(e) { setField('publishAt', e.target.value ? new Date(e.target.value).toISOString() : null); }}
                                            style={{ height: 42, fontSize: 14 }}
                                        />
                                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
                                            У майбутньому — відкладена публікація
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 6 }}>
                                            <FontAwesomeIcon icon={faCalendarXmark} style={{ marginRight: 5, fontSize: 11 }} />
                                            Показувати до
                                            <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>(необов'язково)</span>
                                        </label>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                value={toLocalDatetimeInput(news.expireAt)}
                                                onChange={function(e) { setField('expireAt', e.target.value ? new Date(e.target.value).toISOString() : null); }}
                                                style={{ height: 42, fontSize: 14 }}
                                            />
                                            {news.expireAt && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={function() { setField('expireAt', null); }}
                                                    style={{ height: 42, flexShrink: 0 }}
                                                    title="Прибрати обмеження"
                                                >
                                                    <FontAwesomeIcon icon={faXmark} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Закріпити */}
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                                        <input
                                            type="checkbox"
                                            checked={news.isPinned}
                                            onChange={function(e) { setField('isPinned', e.target.checked); }}
                                        />
                                        <FontAwesomeIcon icon={faThumbtack} style={{ fontSize: 12, color: '#0069ff' }} />
                                        Закріпити зверху списку
                                    </label>
                                </div>

                                {/* Текст новини */}
                                <div style={{ marginBottom: isEdit ? 20 : 0 }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 8 }}>
                                        Текст новини
                                    </label>
                                    <RichEditor
                                        value={news.body}
                                        onChange={function(html) { setField('body', html); }}
                                        placeholder="Повний текст новини..."
                                        maxLength={20000}
                                        minHeight={220}
                                        maxHeight={480}
                                    />
                                </div>

                                {/* Галерея — тільки в режимі редагування (потрібен newsId) */}
                                {isEdit ? (
                                    <NewsGallery scope={scope} kgId={kgId} newsId={newsId} />
                                ) : (
                                    <div style={{
                                        padding: '14px 16px', background: '#f9fafb', borderRadius: 8,
                                        fontSize: 12, color: '#9ca3af', textAlign: 'center',
                                    }}>
                                        Галерея фото та відео буде доступна після створення новини
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Футер */}
                    <div className="modal-footer" style={{ borderTop: '1px solid #f0f0f0' }}>
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                            Скасувати
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || loading}
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                            {saving ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSave} />}
                            {isEdit ? 'Зберегти зміни' : 'Створити чернетку'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsEditor;